import { useRef } from "react";

import { useAdjustIcpMutation } from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";

import { IcpInfo } from "../../schema/icp";
import { sleep } from "../../pages/onboardingChatTiming";

import { IcpSummaryText } from "./IcpSummaryText";
import { ChatScript } from "./useChatScript";

interface IcpConfirmStepOptions {
	chat: ChatScript;
	/** Translator scoped to `onboarding.chat.`. */
	ts: (key: string) => string;
	/** True once the chat unmounted — every await has to re-check it. */
	isCancelled: () => boolean;
	/** Pause between a user answer and the reply that follows it. */
	beatMs: number;
	/** The targeting is settled — run whichever beat comes next. */
	onSettled: () => void;
}

export interface IcpConfirmStep {
	/** Presents the guess with its yes / adjust choice. */
	showGuess: (icp: IcpInfo, intro: string) => void;
	handleSendAdjust: (text: string) => Promise<void>;
}

/**
 * The "here's who I think you target" beat of the onboarding chat: the guess,
 * the yes/adjust choice, and folding a free-text correction back into the ICP.
 *
 * Confirming does not close the question (PREC-3904). The summary keeps a
 * standing "let me adjust" chip, so a user who clicked "Yes, that's right!" on
 * a guess they had not really read can still correct it — before, the only way
 * back was to restart the onboarding. `onSettled` is what makes that safe: the
 * page decides where the script resumes, so a late correction does not re-ask a
 * question the user already answered.
 */
export const useIcpConfirmStep = ({
	chat,
	ts,
	isCancelled,
	beatMs,
	onSettled,
}: IcpConfirmStepOptions): IcpConfirmStep => {
	const [adjustIcp] = useAdjustIcpMutation();
	/**
	 * A standing chip is clickable while its own correction is still open, and it
	 * carries the closure it was added in — so the open/closed state is read off a
	 * ref rather than off `chat.inputMode`, which is stale inside that closure.
	 */
	const adjustingRef = useRef(false);

	const startAdjust = async () => {
		if (adjustingRef.current) {
			return;
		}

		adjustingRef.current = true;
		chat.clearChips();
		chat.addUser(ts("chipAdjust"));
		chat.setIsTyping(true);
		await sleep(beatMs);

		if (isCancelled()) {
			return;
		}

		chat.setIsTyping(false);
		chat.addAi(<>{ts("adjustPrompt")}</>);
		chat.setInputMode("adjust");
	};

	/** The correction is resolved — the chip is offered again. */
	const endAdjust = () => {
		adjustingRef.current = false;
	};

	/**
	 * The settled targeting, plus what it is actually used for — users read the
	 * ICP as "who you will find for me" and are then surprised that it also seeds
	 * the scoring, the knowledge base and the research (PREC-3904).
	 */
	const showSummary = (icp: IcpInfo, intro: string) => {
		chat.addAi(
			<>
				{intro} <IcpSummaryText icp={icp} />.
				<br />
				<br />
				{ts("feedsNote")}
			</>,
			[{ label: ts("chipAdjust"), onClick: () => void startAdjust(), sticky: true }]
		);
	};

	const showGuess = (icp: IcpInfo, intro: string) => {
		chat.addAi(
			<>
				{intro} <IcpSummaryText icp={icp} />.
				<br />
				<br />
				{ts("confirmQuestion")}
			</>,
			[
				{ label: ts("chipYes"), onClick: () => void handleConfirmGuess(icp) },
				{ label: ts("chipAdjust"), onClick: () => void startAdjust() },
			]
		);
	};

	const handleConfirmGuess = async (icp: IcpInfo) => {
		chat.clearChips();
		chat.addUser(ts("chipYes"));
		chat.setIsTyping(true);
		await sleep(beatMs);

		if (isCancelled()) {
			return;
		}

		chat.setIsTyping(false);
		showSummary(icp, ts("confirmedIntro"));
		onSettled();
	};

	const handleSendAdjust = async (text: string) => {
		chat.addUser(text);
		chat.setInputMode("hidden");
		chat.setIsTyping(true);

		try {
			const icp = await adjustIcp({ message: text }).unwrap();

			if (isCancelled()) {
				return;
			}

			chat.setIsTyping(false);

			// The model sometimes echoes the ICP back unchanged — saying "I've
			// updated your targeting to <the same thing>" reads as a broken step.
			if (!icp.changed) {
				chat.addAi(<>{ts("adjustNoChange")}</>);
				chat.setInputMode("adjust");

				return;
			}

			endAdjust();
			showSummary(icp, ts("updatedIntro"));
			onSettled();
		} catch (err) {
			console.error(err);

			if (isCancelled()) {
				return;
			}

			chat.setIsTyping(false);
			chat.addAi(<>{ts("adjustFailed")}</>);
			chat.setInputMode("adjust");
		}
	};

	return { showGuess, handleSendAdjust };
};
